import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { OfferteStappen } from "@/components/offerte/OfferteStappen";
import { VerifyEmailSent } from "@/components/auth/VerifyEmailSent";
import { RegisterTopFields } from "@/components/auth/RegisterTopFields";
import { RegisterExtraFields } from "@/components/auth/RegisterExtraFields";
import { startCheckout } from "@/lib/payments";
import { isFreePeriod } from "@/lib/pricing";
import { useSeo } from "@/lib/seo";
import { HardHat, Wrench, Building2, User } from "lucide-react";
import { toast } from "sonner";

export default function Auth({ mode }) {
  const isLogin = mode === "login";
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const intent = params.get("intent");
  const isOfferteIntent = intent === "offerte";
  const { login, register } = useAuth();
  useSeo(
    isLogin
      ? { title: "Inloggen | Work4you", description: "Log in op je Work4you-account om opdrachten, offertes en je profiel te beheren.", path: "/inloggen" }
      : { title: "Registreren als professional, bedrijf of particulier | Work4you", description: "Maak gratis een Work4you-account aan als professional, bedrijf of particulier en koppel vraag en aanbod voor ramen- en deurenmontage.", path: "/registreren", canonicalPath: "/registreren" }
  );
  const [loading, setLoading] = useState(false);
  const [role, setRole] = useState("monteur");
  const [vatState, setVatState] = useState({ checking: false, result: null });
  const [registered, setRegistered] = useState(null);
  const [unverifiedEmail, setUnverifiedEmail] = useState(null);
  const [form, setForm] = useState({
    email: "", password: "", name: "", telefoon: "", regio: "",
    specialisatie: "beide", ervaring_jaren: "", uurtarief: "", bedrijfsnaam: "",
    btw_nummer: "", adres: "", provincie: "", op_zoek_naar: [],
    website: "", ik_bied_aan: [], aanbod_voor: [], aanbod_andere: "",
    materialen: [], merken_pvc: [], merken_pvc_andere: "",
    merken_alu: [], merken_alu_andere: "", merken_hout: [], merken_hout_andere: "",
    plaatst_voor_collega: false,
  });

  useEffect(() => {
    const r = params.get("rol");
    if (r === "bedrijf" || r === "monteur" || r === "particulier") setRole(r);
  }, [params]);

  useEffect(() => {
    if (!isOfferteIntent) return;
    setRole("particulier");
    try {
      const draft = JSON.parse(sessionStorage.getItem("offerte_draft") || "null");
      if (draft) {
        const adres = [draft.adres_straat, [draft.adres_postcode, draft.adres_gemeente].filter(Boolean).join(" ")].filter(Boolean).join(", ");
        setForm((f) => ({
          ...f,
          name: draft.naam || f.name,
          email: draft.email || f.email,
          telefoon: draft.telefoon || f.telefoon,
          adres: adres || f.adres,
        }));
      }
    } catch (e) { /* geen draft */ }
  }, [isOfferteIntent]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target?.value ?? e });

  const validateVat = async () => {
    const btw = (form.btw_nummer || "").trim();
    if (!btw) {
      toast.error("Vul eerst een BTW-nummer in.");
      return;
    }
    setVatState({ checking: true, result: null });
    try {
      const { data } = await api.post("/validate-vat", { btw_nummer: btw });
      setVatState({ checking: false, result: data });
      if (data.geldig) {
        setForm((f) => ({
          ...f,
          btw_nummer: data.btw_nummer || f.btw_nummer,
          bedrijfsnaam: data.bedrijfsnaam || f.bedrijfsnaam,
          regio: data.adres || f.regio,
        }));
        toast.success("BTW-nummer geldig — gegevens ingevuld.");
      } else if (data.status === "onbekend") {
        toast.info("VIES is tijdelijk onbereikbaar. Je kunt toch verder.");
      } else {
        toast.error(data.boodschap || "BTW-nummer ongeldig.");
      }
    } catch (err) {
      setVatState({ checking: false, result: null });
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Validatie mislukt.");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isLogin) {
        const user = await login(form.email, form.password);
        if (isOfferteIntent && user?.role === "particulier") {
          const draft = JSON.parse(sessionStorage.getItem("offerte_draft") || "null");
          if (draft) {
            const { data } = await api.post("/offertes", draft);
            sessionStorage.removeItem("offerte_draft");
            if (data.payment_required) {
              toast.success("Rond de €3-betaling af om je aanvraag te versturen.");
              await startCheckout({ kind: "offerte", offerteId: data.offerte.id });
              return;
            }
            toast.success("Je aanvraag is gratis verstuurd naar de gekozen bedrijven.");
          }
        }
        toast.success("Welkom terug!");
        navigate(user.role === "monteur" ? "/monteur" : (user.role === "bedrijf" || user.role === "particulier") ? "/opdrachtgever" : "/admin");
        return;
      }

      const payload = {
        email: form.email, password: form.password, name: form.name,
        role, telefoon: form.telefoon, regio: form.regio,
      };
      if (role === "monteur") {
        payload.specialisatie = form.specialisatie;
        payload.ervaring_jaren = parseInt(form.ervaring_jaren || "0", 10);
        payload.uurtarief = parseFloat(form.uurtarief || "0");
        payload.werkstatuut = form.werkstatuut || "";
        payload.vervoer = form.vervoer || "";
        payload.rijbewijs_europa = !!form.rijbewijs_europa;
        payload.rijbewijs_categorieen = form.rijbewijs_categorieen || [];
        payload.werkmateriaal = form.werkmateriaal || "";
        payload.spec_montage = form.spec_montage || [];
        payload.spec_montage_andere = form.spec_montage_andere || "";
        payload.spec_afwerking = form.spec_afwerking || [];
        payload.spec_afwerking_andere = form.spec_afwerking_andere || "";
        payload.leiding_team = !!form.leiding_team;
        payload.talen = form.talen || [];
        payload.moedertaal = form.moedertaal || "";
        payload.andere_talen = form.andere_talen || "";
        payload.bio = form.bio || "";
      } else if (role === "bedrijf") {
        payload.bedrijfsnaam = form.bedrijfsnaam || form.name;
        payload.btw_nummer = form.btw_nummer || "";
        payload.website = form.website || "";
        payload.ik_bied_aan = form.ik_bied_aan || [];
        payload.aanbod_voor = form.aanbod_voor || [];
        payload.aanbod_andere = form.aanbod_andere || "";
        payload.materialen = form.materialen || [];
        payload.merken_pvc = form.merken_pvc || [];
        payload.merken_pvc_andere = form.merken_pvc_andere || "";
        payload.merken_alu = form.merken_alu || [];
        payload.merken_alu_andere = form.merken_alu_andere || "";
        payload.merken_hout = form.merken_hout || [];
        payload.merken_hout_andere = form.merken_hout_andere || "";
        payload.plaatst_voor_collega = !!form.plaatst_voor_collega;
      } else if (role === "particulier") {
        payload.adres = form.adres || "";
        payload.op_zoek_naar = form.op_zoek_naar || [];
      }
      payload.provincie = form.provincie || "";

      if (isOfferteIntent && role === "particulier") {
        const draft = JSON.parse(sessionStorage.getItem("offerte_draft") || "null");
        if (draft) payload.offerte = draft;
      }

      await register(payload);
      sessionStorage.removeItem("offerte_draft");
      setRegistered(form.email);
    } catch (err) {
      const detail = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      if (err.response?.status === 403 && isLogin) setUnverifiedEmail(form.email);
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  const resendVerification = async (email) => {
    try {
      await api.post("/auth/resend-verification", { email });
      toast.success("Nieuwe bevestigingslink verstuurd. Controleer je inbox.");
    } catch (e) {
      toast.error("Versturen mislukt. Probeer later opnieuw.");
    }
  };

  if (registered) {
    return <VerifyEmailSent email={registered} isOfferteIntent={isOfferteIntent} onResend={resendVerification} />;
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between bg-slate-950 text-white p-12">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-9 h-9 bg-orange-500 flex items-center justify-center rounded-sm">
            <HardHat className="w-5 h-5 text-white" />
          </div>
          <span className="font-display text-lg font-extrabold">Work4you</span>
        </Link>
        <div>
          <h2 className="font-display text-4xl font-black leading-tight mb-4">
            De professional achter elke <span className="text-orange-500">klus</span>.
          </h2>
          <p className="text-slate-400 max-w-md">
            Het platform voor dienstverlenende professionals en hun klanten. Koppel vraag en aanbod zonder gedoe.
          </p>
        </div>
        <p className="text-slate-600 text-sm">© 2026 Work4you</p>
      </div>

      {/* Form */}
      <div className="flex items-center justify-center p-6 lg:p-12 bg-slate-50">
        <div className="w-full max-w-md">
          <Link to="/" className="lg:hidden flex items-center gap-2 mb-8">
            <div className="w-8 h-8 bg-orange-500 flex items-center justify-center rounded-sm">
              <HardHat className="w-4 h-4 text-white" />
            </div>
            <span className="font-display font-extrabold">Work4you</span>
          </Link>
          <p className="label-caps mb-2">{isLogin ? "Welkom terug" : "Aan de slag"}</p>
          <h1 className="font-display text-3xl font-extrabold text-slate-950 mb-8">
            {isLogin ? "Inloggen" : "Account aanmaken"}
          </h1>

          {isOfferteIntent && (
            <>
            <OfferteStappen active={2} />
            <div data-testid="offerte-fee-notice-auth" className="mb-6 bg-slate-950 text-white rounded-md px-5 py-4">
              {isFreePeriod() ? (
                <>
                  <p className="font-display font-bold text-green-400 mb-1">Laatste stap — en het is volledig gratis</p>
                  <p className="text-sm text-slate-200 leading-relaxed">
                    Maak je account aan en je aanvraag wordt meteen verstuurd. <strong>Gratis tot 31 december 2026</strong> — geen kosten, geen abonnement.
                  </p>
                </>
              ) : (
                <>
                  <p className="font-display font-bold text-orange-400 mb-1">Laatste stap — je prijsaanvraag kost €3</p>
                  <p className="text-sm text-slate-200 leading-relaxed">
                    Maak je account aan en rond de betaling van €3 af om je aanvraag te versturen.
                    Deze kleine bijdrage voorkomt misbruik en valse aanvragen. Je krijgt de <strong>€3 volledig terug</strong> bij een eventuele bestelling bij één van de aangevraagde bedrijven.
                  </p>
                </>
              )}
            </div>
            </>
          )}

          {!isLogin && (
            <div className="grid grid-cols-3 gap-2 mb-6">
              {[
                { key: "monteur", label: "Professional", icon: Wrench },
                { key: "bedrijf", label: "Bedrijf", icon: Building2 },
                { key: "particulier", label: "Particulier", icon: User },
              ].map((r) => (
                <button
                  key={r.key}
                  type="button"
                  data-testid={`role-${r.key}-btn`}
                  onClick={() => setRole(r.key)}
                  className={`flex flex-col items-center gap-1 justify-center border rounded-sm py-3 font-semibold text-xs transition-colors duration-200 ${
                    role === r.key
                      ? "bg-orange-500 border-orange-500 text-white"
                      : "bg-white border-slate-200 text-slate-600 hover:border-slate-400"
                  }`}
                >
                  <r.icon className="w-4 h-4" /> {r.label}
                </button>
              ))}
            </div>
          )}

          <form onSubmit={submit} className="space-y-4">
            {!isLogin && (
              <RegisterTopFields role={role} form={form} set={set} vatState={vatState} validateVat={validateVat} />
            )}
            <div>
              <Label className="text-xs font-bold text-slate-700">E-mailadres</Label>
              <Input data-testid="email-input" type="email" required value={form.email} onChange={set("email")}
                className="rounded-sm border-slate-300" placeholder="naam@bedrijf.nl" />
            </div>
            <div>
              <Label className="text-xs font-bold text-slate-700">Wachtwoord</Label>
              <Input data-testid="password-input" type="password" required value={form.password} onChange={set("password")}
                className="rounded-sm border-slate-300" placeholder="Minimaal 6 tekens" />
              {isLogin && (
                <div className="text-right mt-1.5">
                  <Link data-testid="forgot-password-link" to="/wachtwoord-vergeten"
                    className="text-xs text-orange-600 font-semibold hover:underline">
                    Wachtwoord vergeten?
                  </Link>
                </div>
              )}
            </div>

            {!isLogin && (
              <RegisterExtraFields role={role} form={form} set={set} setForm={setForm} />
            )}

            {isLogin && unverifiedEmail && (
              <div data-testid="login-unverified-block" className="bg-amber-50 border border-amber-200 rounded-md px-4 py-3 text-sm text-amber-800">
                <p className="font-semibold mb-2">Je e-mailadres is nog niet bevestigd.</p>
                <Button type="button" data-testid="login-resend-verification-btn" onClick={() => resendVerification(unverifiedEmail)}
                  variant="outline" className="rounded-sm font-semibold border-amber-300 text-amber-800 hover:bg-amber-100">
                  Verstuur bevestigingslink opnieuw
                </Button>
              </div>
            )}

            <Button data-testid="auth-submit-btn" type="submit" disabled={loading}
              className="w-full bg-orange-500 hover:bg-orange-600 text-white rounded-sm font-bold h-11">
              {loading ? "Bezig..." : isLogin ? "Inloggen" : "Account aanmaken"}
            </Button>
          </form>

          <p className="text-center text-sm text-slate-500 mt-6">
            {isLogin ? "Nog geen account? " : "Al een account? "}
            <Link data-testid="toggle-auth-link" to={`${isLogin ? "/registreren" : "/inloggen"}${isOfferteIntent ? "?intent=offerte" : ""}`}
              className="text-orange-600 font-semibold hover:underline">
              {isLogin ? "Registreren" : "Inloggen"}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
